import { createRouter, createWebHistory } from '@ionic/vue-router';
import { modalController } from '@ionic/vue';
import store from '@/store';
import Tabs from '@/views/Tabs.vue';

import { checkForSameOrigin } from '@/authentication-caching-utils';

const BASE_URL = process.env.VUE_APP_BASE_URL;

import { Plugins } from '@capacitor/core';
const { App } = Plugins;

import { INVERSE_QR_URL_PATH_MAPPING } from '@/components/composables/QR';

import _ from 'lodash';

//Define all used side menu components here
import Tracking from '@/views/Tracking.vue'
import SkeletonPointSelect from '@/views/Tools/SkeletonPointSelect.vue'

const routes = [
  {
    path: '/',
    redirect: '/health'
  },
  {
    path: '/login',
    name: 'login',
    meta: { noAuthenticationRequired: true, hideWhenAuthenticated: true },
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/register',
    name: 'register',
    meta: { noAuthenticationRequired: true, hideWhenAuthenticated: true },
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    meta: { noAuthenticationRequired: true, hideWhenAuthenticated: true },
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/set-password',
    name: 'set-password',
    meta: { noAuthenticationRequired: true, hideWhenAuthenticated: true },
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/health/',
    component: Tabs,
    meta: { sideMenuComponent: Tracking, sideMenuMediaQuery: undefined }, //Only needed in parent route, is inherited
    children: [ //Params need to be unique, because View might still be active when routing and then params change!
      {
        path: '',
        redirect: '/health/animals'
      },
      {
        path: 'record',
        name: 'recent-animals',
        component: () => import('@/views/Analysis/Recents.vue'),
        beforeEnter: (to, from, next) => {
          let selectedAnimal = store.getters['horses/getSelectedHorse'];

          //If an animal was before selected to analyse/view, select it again, if coming back from another tab
          if (selectedAnimal != null) {
            //Check if we came from the same tab, then reset the selected horse
            if (_.startsWith(from.fullPath, '/health/record') || (to.query != null && to.query['reset_selection'])) {
              store.dispatch('horses/updateSelectedHorse', null);
              next({ path: to.path, query: _.omit(to.query, 'reset_selection'), hash: to.hash });
              return;
            } else { //From another tab not matching this route, we move to the animal view with the selected id
              next({ name: 'view-animal', params: { 'animalId': selectedAnimal } });
              return;
            }
          }
          //If nothing matches, just continue
          next();
        }
      },
      {
        path: 'record/:animalId',
        name: 'view-animal',
        component: () => import('@/views/AnimalRecord.vue'),
        beforeEnter: (to, from, next) => {
          //If we enter this view, set the selected animal to this id in store
          store.dispatch('horses/updateSelectedHorse', to.params['animalId']);
          next();
        }
      },
      {
        path: 'animals',
        meta: { isHome: true }, //Used to determine redirects
        name: 'subject-overview',
        component: () => import('@/views/AnimalManagement.vue')
      },
      {
        path: 'record/report/:id',
        name: 'view-report',
        component: () => import('@/views/Analysis/ViewReport.vue')
      },
      {
        path: 'record/reports/:connection_id',
        name: 'view-connected-reports',
        component: () => import('@/views/Analysis/ViewReport.vue')
      },
      {
        path: 'record/report/create/:types',
        name: 'create-report',
        meta: { sideMenuComponent: undefined, sideMenuMediaQuery: undefined, hideTabNavigation: true },
        component: () => import('@/views/Analysis/CreateReport.vue')
      },
      {
        path: 'record/report/update/:update_id',
        name: 'update-report',
        meta: { sideMenuComponent: undefined, sideMenuMediaQuery: undefined, hideTabNavigation: true },
        component: () => import('@/views/Analysis/CreateReport.vue')
      },
      {
        path: 'record/reports/update/:update_connection_id',
        name: 'update-connected-reports',
        meta: { sideMenuComponent: undefined, sideMenuMediaQuery: undefined, hideTabNavigation: true },
        component: () => import('@/views/Analysis/CreateReport.vue')
      },
      {
        path: 'encyclopedia',
        name: 'encyclopedia',
        component: () => import('@/views/Encyclopedia.vue')
      },
      {
        path: 'encyclopedia/type/:type_identifier',
        name: 'encyclopedia-type',
        component: () => import('@/views/Encyclopedia.vue')
      },
      {
        path: 'encyclopedia/article/:identifier',
        name: 'encyclopedia-entry',
        component: () => import('@/views/Encyclopedia.vue')
      },
      //Legacy route mapping
      {
        path: 'animals/:animalId',
        redirect: (to) => {
          return ({name: 'view-animal', params: to.params, hash: to.hash});
        }
      },
      {
        path: 'analysis/report/:id',
        redirect: (to) => {
          return ({name: 'view-report', params: to.params, hash: to.hash});
        }
      },
      {
        path: 'analysis/reports/:connection_id',
        redirect: (to) => {
          return ({name: 'view-connected-reports', params: to.params, hash: to.hash});
        }
      },
      {
        path: 'analysis/report/create/:types',
        redirect: (to) => {
          return ({name: 'create-report', params: to.params, hash: to.hash});
        }
      },
      {
        path: 'analysis/report/update/:update_id',
        redirect: (to) => {
          return ({name: 'update-report', params: to.params, hash: to.hash});
        }
      },
      {
        path: 'analysis/reports/update/:update_connection_id',
        redirect: (to) => {
          return ({name: 'update-connected-reports', params: to.params, hash: to.hash});
        }
      }
    ]
  },
  /*{//FIXME Greift irgendwie schon der Parent auf die Kamera zu, wenn wir in ai-eye drin waren
    path: '/record/ai-eye', //TODO Nur die Navigation erlauben, wenn registriert, damit AGB angenommen werden können? Dann Analyse auch nur zulassen, wenn Anfrage von registriertem Nutzer kommt
    //TODO Oder bei jeder Analyse annehmen der AGB und der Nutzungsrechte und Verständniserklärung, dass keine Diagnose ist fordern. Für registrierte Nutzer nicht, da die das einmal festlegen und dann gespiechert wird.
    //TODO Könnte man lösen indem man einen Guest Zugang macht, der Limited Access auf die API und Co. hat aber trotzdem als authenticated zählt. Zusätzliches Property irgendwie einfügen.
    //TODO Oder doch anzeigen, wenn man nicht authenticated ist. Könnte aber für angemeldete Nutzer verwirrend sein, die in dem Moment rausfliegen, wenn sie auf so eine Seite gehen.
    component: () => import('@/views/Analysis/AIEye.vue')
  },*/
  {
    path: '/tools',
    name: 'tools',
    meta: { needsPermission: 'Tools' },
    component: () => import('@/views/Tools.vue')    
  },
  {
    path: '/tools/label-skeleton',
    meta: { sideMenuComponent: SkeletonPointSelect, needsPermission: 'Horse_Pose_Tools' },
    component: () => import('@/views/Tools/LabelSkeleton.vue')
  },
  {
    path: '/tools/analyse-skeleton',
    meta: { needsPermission: 'Horse_Pose_Tools' },
    component: () => import('@/views/Tools/AnalyseSkeleton.vue')
  },
  {
    path: '/tools/design-report',
    meta: { needsPermission: 'Can_Design_Entry_Types' },
    component: () => import('@/views/Tools/DesignReport.vue')
  },
  {
    path: '/tools/upload-reports',
    meta: { needsPermission: 'Can_Edit_Entry_Types' },
    component: () => import('@/views/Tools/UploadReports.vue')
  },
  {
    path: '/tools/upload-encyclopedia',
    meta: { needsPermission: 'Content_Editor' },
    component: () => import('@/views/Tools/UploadEncyclopedia.vue')
  },
  {
    path: '/tools/user-verification',
    meta: { needsPermission: 'User_Verification' },
    component: () => import('@/views/Tools/UserVerification.vue')
  },
  {
    path: '/general-feedback',
    name: 'general-feedback',
    component: () => import('@/views/GeneralFeedback.vue')
  },
  //Deep Links
  {
    path: '/link/:type/:id',
    name: 'link',
    beforeEnter: (to, from, next) => {
      //TODO Could always forward the code to a modal to handle unassigned codes!
      if (to != null && to.params != null && to.params.type != null && to.params.id != null) {
        let type = INVERSE_QR_URL_PATH_MAPPING[to.params.type];
        if (type != null) {
          switch (type) {
            case 'animal': {
              let animal = store.getters['horses/getHorseById'](to.params.id);
              
              if (animal != null) {
                next({ name: 'view-animal', params: { 'animalId': animal.id } });
                return;
              }
              break;
            }
            
            case 'location':
              store.commit('horses/setGroupByStatusState', false);
              next({name: 'subject-overview', hash: `#${to.params.id}`});
              return;
            case 'case-status':
              store.commit('horses/setGroupByStatusState', true);
              next({name: 'subject-overview', hash: `#${to.params.id}`});
              return;

            case 'report':
              next({ name: 'view-report', params: { 'id': to.params.id } });
              return;

            case 'encyclopedia':
              next({ name: 'encyclopedia-entry', params: { 'identifier': to.params.id } });
              return;

            default:
              break;
          }
        }
      }
      
      //TODO Check If from is empty, maybe go to health, otherwise return false and go back?
      next({name: 'subject-overview'}); //Never allow routing, always move somewhere else if not caught before!
    }
  },
  { //Catch all other routes, 404, redirect to homepage
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  //Prevent navigation when a modal is open!
  try {
    if (await modalController.getTop() != null) {
      next(false);
      return;
    }
  } catch (error) {
    console.error("Error getting modals on navigation", error);
  }
  
  if (store.getters['auth/isAuthenticated'] === true){
    //Catch the case where an authenticated user gets redirected to e.g. login or register without a redirect
    if (to.meta.hideWhenAuthenticated){
      //Keep the query for e.g. redirect after login
      next({ path : '/', query: to.query });
    } else if (to.meta.needsPermission != null) {
      if (store.getters['auth/hasPermission'](to.meta.needsPermission)) {
        next();
      } else {
        next('/');
      }
    } else {
      next();
    }
  } else { //User is not authenticated
    if (to.meta.noAuthenticationRequired) { //Correct navigation to login after authentication change i.e. logout or a page where no authentication is needed, do not save where they logged out for a fresh start on login
      next();  
    } else { //Tried to navigate without permission - Prevent further navigation if not logged in
      let newPath = { name: 'login' };
      newPath['query'] = {};
      if (to.query != null) {
        newPath['query'] = {...(to.query)};
      }
      //If it is not home, redirect
      if (!(to.meta != null && to.meta.isHome)) {
        newPath['query']['redirect'] = to.fullPath;
      }
      next(newPath);
    }
  }
});

//TODO Test back button behaviour. If it behaves differently, disable it in certain routes:
/**
 * App.addListener('backButton', () => {});
 * 
 */

App.addListener('appUrlOpen', function (data) {
  if (data != null && data.url != null) {
    try {
      let parsedURL = checkForSameOrigin(data.url, BASE_URL, undefined, true);

      if (parsedURL != null) {
        let query = {};

        if (parsedURL.searchParams != null) {
          for (let [key, value] of parsedURL.searchParams.entries()) {
            if (key != null && value != null && key.length > 0 && value.length > 0) {
              query[key] = value;
            }
          }
        }

        router.push({
          path: parsedURL.pathname,
          query
        });
      }
    } catch (error) {
      console.error(`Could not open app link: ${data.url}`, error)
    }
  }
});

export default router
